import { Chart } from 'react-chartjs-2';
import { useGetByIdAndTimeSummarizedQuery } from '../../../services/historicalData';
import { useEffect, useMemo, useState } from 'react';
import { Block } from 'baseui/block';
import { addDays, addMonths, format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import { datasetFromData, ICounterAndData } from './utils';
import { ICounter } from '../../Devices/Counter';
import { Banner } from 'baseui/banner';
import { SingleMonthlyEnergy } from './';
import { defaultOptions } from './defaultOptions';
import { useGetLanguage } from '../../../hooks/useGetLanguage';
import React from 'react';
import { CounterMedium } from '../../Devices/CounterMedium';
import { getMediumColor } from '../../../utils/colorUtils';
import { themedUseStyletron } from '../../../theme/customTheme';


export interface EnergyBarChartProps {
    counters: ICounter[],
    propertyName: string,
    dimension: string,
    currencyCode?: string | undefined,
    squareMeter: number
    showChart: boolean
}
interface IState {
    start: Date,
    end: Date,
    datasets: any[],
    currentMonthData: ICounterAndData[],
    monthOffset2Data: ICounterAndData[],
    monthOffset3Data: ICounterAndData[],
}

function EnergyBarChart({ counters, propertyName, dimension, squareMeter, showChart, currencyCode }: EnergyBarChartProps) {
    const intl = useIntl();
    const { locale } = useGetLanguage();
    const [css, theme] = themedUseStyletron();
    const current = new Date();
    const [state, setState] = useState<IState>({
        start: addMonths(new Date(current.getFullYear(), current.getMonth(), current.getDate()), -12),
        end: addDays(new Date(current.getFullYear(), current.getMonth(), current.getDate()), 1),
        datasets: [],
        currentMonthData: [],
        monthOffset2Data: [],
        monthOffset3Data: []
    });
    const myOptions = { ...defaultOptions };
    myOptions.scales.x.adapters = {
        date: {
            locale: locale
        }
    };


    // update every x seconds
    useEffect(() => {
        const interval = setInterval(() => UpdateTimes(), 60 * 1000); // every 5 minutes
        return () => {
            clearInterval(interval);
        };
    });
    function UpdateTimes() {
        setState((prev) => ({
            ...prev,
            start: addMonths(new Date(current.getFullYear(), current.getMonth(), current.getDate()), -12),
            end: addDays(new Date(current.getFullYear(), current.getMonth(), current.getDate()), 1),
        }));
    }

    const { data: data1 } = useGetByIdAndTimeSummarizedQuery(
        {
            id: counters[0]?.id,
            resourceGroupId: counters[0]?.resourceGroupId,
            propertyName: propertyName,
            start: state.start.toISOString(),
            end: state.end.toISOString(),
            summarizeBy: 'month'
        }, { skip: counters.length < 1 || !counters[0]?.id });
    const { data: data2 } = useGetByIdAndTimeSummarizedQuery(
        {
            id: counters[1]?.id,
            resourceGroupId: counters[1]?.resourceGroupId,
            propertyName: propertyName,
            start: state.start.toISOString(),
            end: state.end.toISOString(),
            summarizeBy: 'month'
        }, { skip: counters.length < 2 || !counters[1]?.id });
    const { data: data3 } = useGetByIdAndTimeSummarizedQuery(
        {
            id: counters[2]?.id,
            resourceGroupId: counters[2]?.resourceGroupId,
            propertyName: propertyName,
            start: state.start.toISOString(),
            end: state.end.toISOString(),
            summarizeBy: 'month'
        }, { skip: counters.length < 3 || !counters[2]?.id });
    const { data: data4 } = useGetByIdAndTimeSummarizedQuery(
        {
            id: counters[3]?.id,
            resourceGroupId: counters[3]?.resourceGroupId,
            propertyName: propertyName,
            start: state.start.toISOString(),
            end: state.end.toISOString(),
            summarizeBy: 'month'
        }, { skip: counters.length < 4 || !counters[3]?.id });
    const { data: data5 } = useGetByIdAndTimeSummarizedQuery(
        {
            id: counters[4]?.id,
            resourceGroupId: counters[4]?.resourceGroupId,
            propertyName: propertyName,
            start: state.start.toISOString(),
            end: state.end.toISOString(),
            summarizeBy: 'month'
        }, { skip: counters.length < 5 || !counters[4]?.id });
    const { data: data6 } = useGetByIdAndTimeSummarizedQuery(
        {
            id: counters[5]?.id,
            resourceGroupId: counters[5]?.resourceGroupId,
            propertyName: propertyName,
            start: state.start.toISOString(),
            end: state.end.toISOString(),
            summarizeBy: 'month'
        }, { skip: counters.length < 6 || !counters[5]?.id });


    useEffect(() => {
        if (!data1)
            return;

        var datas: ICounterAndData[] = [];
        if (counters.length >= 1) datas.push({ counter: counters[0], data: data1 ?? [] });
        if (counters.length >= 2) datas.push({ counter: counters[1], data: data2 ?? [] });
        if (counters.length >= 3) datas.push({ counter: counters[2], data: data3 ?? [] });
        if (counters.length >= 4) datas.push({ counter: counters[3], data: data4 ?? [] });
        if (counters.length >= 5) datas.push({ counter: counters[4], data: data5 ?? [] });
        if (counters.length >= 6) datas.push({ counter: counters[5], data: data6 ?? [] });



        //if its kWh we just want to have kWh, Wh and MWh in the dimension and no m³ or so. For CO2 and € its okay
        const filteDatas = dimension === 'kWh' ? [...datas].filter((item, index) => item.counter.dimension === 'kWh' || item.counter.dimension === 'Wh' || item.counter.dimension === 'MWh') : datas;


        const thisMonthData: ICounterAndData[] = filteDatas.map((item) => { return { counter: item.counter, data: [item.data[item.data.length - 1]] } });
        const lastMonthData: ICounterAndData[] = filteDatas.map((item) => { return { counter: item.counter, data: item.data.length - 2 >= 0 ? [item.data[item.data.length - 2]] : [] } });
        const monthOffset3: ICounterAndData[] = filteDatas.map((item) => { return { counter: item.counter, data: item.data.length - 3 >= 0 ? [item.data[item.data.length - 3]] : [] } });

        const dscounters = filteDatas.map((item, index) => datasetFromData(item, getMediumColor(item.counter.counterMedium,item.counter.counterType, theme), intl));

        setState((prev) => ({
            ...prev,
            datasets: dscounters,
            currentMonthData: thisMonthData,
            monthOffset2Data: lastMonthData,
            monthOffset3Data: monthOffset3,
        }));

    }, [data1, data2, data3, data4, data5, data6])


    const data = useMemo(() => {
        const tmp = {
            //labels: data1?.map((item) => intl.formatDate(new Date(item.timeStamp), { month: 'short', year: '2-digit' })),
            datasets: state.datasets,
        }
        return tmp;
    }, [data1, state.datasets]);


    const isCurrency = currencyCode !== undefined;
    const dim = isCurrency ? currencyCode : dimension;

    return (
        <Block width={'100%'} display='flex' flexDirection='column' alignItems='center' gridRowGap={theme.sizing.scale300}>
            {counters?.length > 0 ?
                <>
                    <SingleMonthlyEnergy currencyCode={currencyCode} data={state.currentMonthData} isCurrrency={isCurrency} dimension={dim} monthOffset={0} squareMeter={squareMeter}></SingleMonthlyEnergy>
                    <SingleMonthlyEnergy currencyCode={currencyCode} data={state.monthOffset2Data} isCurrrency={isCurrency} dimension={dim} monthOffset={-1} squareMeter={squareMeter}></SingleMonthlyEnergy>
                    <SingleMonthlyEnergy currencyCode={currencyCode} data={state.monthOffset3Data} isCurrrency={isCurrency} dimension={dim} monthOffset={-2} squareMeter={squareMeter}></SingleMonthlyEnergy>


                    {showChart &&
                        <div className={css({ height: '190px', width: '100%' })}>
                            <Chart type='bar' data={data} options={myOptions} />
                        </div>}

                </>
                :
                <Banner kind='warning'>
                    <FormattedMessage id='no counters' defaultMessage="No 'Feed' or 'Produces' counters configured"></FormattedMessage>
                </Banner>
            }
        </Block>
    );
}


export default React.memo(EnergyBarChart)



  